import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { CourseCard } from '../../components/CourseCard';
import { useEmployeeStore } from '../../stores/employeeStore';

interface FilterButtonProps {
    value: string;
    label: string;
    currentFilter: string;
    onClick: (value: string) => void;
}

function FilterButton({ value, label, currentFilter, onClick }: FilterButtonProps) {
    return (
        <button 
            onClick={() => onClick(value)}
            className={`px-4 py-1.5 text-sm font-medium border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 ${
                currentFilter === value ? 'bg-primary-50 text-primary-600 ring-1 ring-primary-200' : 'bg-white text-slate-600'
            }`}
        >
            {label}
        </button>
    );
}

export function MyCoursesPage() {
    const { userCourses, isLoading, error } = useEmployeeStore();
    const navigate = useNavigate();
    const [filter, setFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    
    const coursesArray = Object.values(userCourses);
    const filteredCourses = coursesArray.filter(course => {
        // Apply status filter
        let statusMatch = true;
        if (filter === 'inProgress') statusMatch = course.status === 'inProgress';
        if (filter === 'completed') statusMatch = course.status === 'completed';
        if (filter === 'notStarted') statusMatch = course.status === 'notStarted' || course.status === 'due';
        
        // Apply search filter
        const searchMatch = searchQuery === '' || 
            course.courses.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            course.courses.description.toLowerCase().includes(searchQuery.toLowerCase());
        
        return statusMatch && searchMatch;
    });

    const handleViewCourse = (courseId: string) => {
        const course = userCourses[courseId];
        navigate(`/courses/${courseId}`, { state: { course } });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{error}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                {/* Search Bar */}
                <div className="relative w-full sm:w-80">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-slate-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search courses..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                </div>
                
                {/* Filter Buttons */}
                <div className="flex space-x-2 flex-wrap gap-2">
                    <FilterButton value="all" label="All" currentFilter={filter} onClick={setFilter} />
                    <FilterButton value="inProgress" label="In Progress" currentFilter={filter} onClick={setFilter} />
                    <FilterButton value="notStarted" label="Not Started" currentFilter={filter} onClick={setFilter} />
                    <FilterButton value="completed" label="Completed" currentFilter={filter} onClick={setFilter} />
                </div>
            </div>
            {filteredCourses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredCourses.map(course => (
                        <CourseCard key={course.id} course={course} onViewCourse={handleViewCourse} />
                    ))}
                </div>
            ) : (
                <div className="text-center text-slate-500 mt-10">
                    {searchQuery ? (
                        <p>No courses found matching "{searchQuery}".</p>
                    ) : (
                        <p>No courses match the selected filter.</p>
                    )}
                </div>
            )}
        </div>
    );
}